import React, { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  P,
  Clickable,
  Icon,
  Popover,
  FormSelect,
  Checkbox,
} from '@fashionnovateam/nova-ui-toolkit';
import { ControlledFormInput } from '../../../../Lib/Utils/FormUtils/ControlledFormInput';
import { FiberContentDataGrid } from '../../../SampleTags/AddTags/components/FiberContentDataGrid';
import { useCounterContext } from '../../hooks/useCounter';
import {
  CounterQuoteStatus,
  CounterFabricFormValues,
  counterQuoteStatusDisplayMapping,
  CounterQuoteStatusToHide,
  GarmentFabricDetails,
  SecondaryFabricType,
  CounterFiberContent,
} from '../../types';
import {
  getSecondaryFabricLabel,
  getNextInstanceIndex,
} from '../../utils/fabricMigration';

const FABRIC_TYPE_OPTIONS: Array<{
  type: SecondaryFabricType;
  label: string;
}> = [
  { type: 'lining', label: 'Lining' },
  { type: 'contrast', label: 'Contrast' },
  { type: 'filling', label: 'Filling' },
];

const GARMENT_PART_OPTIONS = [
  { value: 'top', label: 'Top' },
  { value: 'bottom', label: 'Bottom' },
  { value: 'other', label: 'Other' },
];

interface GarmentSectionProps {
  garment?: GarmentFabricDetails;
  index?: number;
  canRemove?: boolean;
}

export function GarmentSection({
  garment,
  index,
  canRemove = false,
}: GarmentSectionProps) {
  const { control, watch, setValue, clearErrors } =
    useFormContext<CounterFabricFormValues>();
  const { selectedCounter } = useCounterContext();

  const quoteStatusToHide = Object.values(CounterQuoteStatusToHide) as string[];
  const quoteStatus = quoteStatusToHide.includes(
    selectedCounter?.counter_quote_status || '',
  )
    ? selectedCounter?.prev_counter_quote_status
    : selectedCounter?.counter_quote_status;

  const formEnabled = useMemo(() => {
    const mappedStatus =
      Object.entries(counterQuoteStatusDisplayMapping).find(
        ([key]) => key === quoteStatus,
      )?.[1] ?? null;

    return (
      mappedStatus === CounterQuoteStatus.NOT_SENT ||
      mappedStatus === CounterQuoteStatus.SENT
    );
  }, [quoteStatus]);

  const isMultiGarment = garment !== undefined && index !== undefined;
  const garments = watch('garments');
  const singleGarmentWeightNA = watch('isFabricWeightNotApplicable') ?? false;
  const singleGarmentPU = watch('hasPolyurethaneCoating') ?? false;

  const isFabricWeightNotApplicable = isMultiGarment
    ? (garment?.isFabricWeightNotApplicable ?? false)
    : singleGarmentWeightNA;

  const hasPolyurethaneCoating = isMultiGarment
    ? (garment?.hasPolyurethaneCoating ?? false)
    : singleGarmentPU;

  const contrast = watch('contrast');
  const lining = watch('lining');
  const filling = watch('filling');
  // Legacy-only (can't add new, but can view/edit/delete existing)
  const backing = watch('backing');
  const coating = watch('coating');
  const singleGarmentSecondaryFabrics = watch('secondaryFabrics') || [];

  const createNewFiber = (): CounterFiberContent => ({
    id: `fiber-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
    name: '',
    percentage: 0,
  });

  const validationRules = (fieldName: string) => ({
    valueAsNumber: fieldName === 'weight',
    min:
      fieldName === 'weight'
        ? {
            value: 0.01,
            message: `${fieldName} must be greater than 0`,
          }
        : undefined,
  });

  const removeGarment = () => {
    if (!garments || index === undefined) return;
    const updatedGarments = garments.filter(
      (g) => g.garmentIndex !== garment?.garmentIndex,
    );
    setValue('garments', updatedGarments);
    clearErrors('garments');
  };

  const updateGarmentPart = (part: string) => {
    if (!garments || index === undefined || !garment) return;
    const updatedGarments = [...garments];
    const garmentIdx = updatedGarments.findIndex(
      (g) => g.garmentIndex === garment.garmentIndex,
    );
    if (garmentIdx !== -1) {
      updatedGarments[garmentIdx] = {
        ...updatedGarments[garmentIdx],
        garmentPart: part as 'top' | 'bottom' | 'other',
      };
      setValue('garments', updatedGarments);
    }
  };

  const updateFabricWeightNotApplicable = (value: boolean) => {
    if (isMultiGarment && garments && garment) {
      const garmentIdx = garments.findIndex(
        (g) => g.garmentIndex === garment.garmentIndex,
      );
      if (garmentIdx !== -1) {
        setValue(
          `garments.${garmentIdx}.isFabricWeightNotApplicable` as keyof CounterFabricFormValues,
          value,
        );
      }
    } else {
      setValue('isFabricWeightNotApplicable', value);
    }
  };

  const updateHasPolyurethaneCoating = (value: boolean) => {
    if (isMultiGarment && garments && garment) {
      const garmentIdx = garments.findIndex(
        (g) => g.garmentIndex === garment.garmentIndex,
      );
      if (garmentIdx !== -1) {
        setValue(
          `garments.${garmentIdx}.hasPolyurethaneCoating` as keyof CounterFabricFormValues,
          value,
        );
      }
    } else {
      setValue('hasPolyurethaneCoating', value);
    }
  };

  const addSecondaryFabric = (type: SecondaryFabricType) => {
    if (isMultiGarment && garments && garment) {
      const updatedGarments = [...garments];
      const garmentIdx = updatedGarments.findIndex(
        (g) => g.garmentIndex === garment.garmentIndex,
      );

      if (garmentIdx !== -1) {
        const g = updatedGarments[garmentIdx];
        const instanceIndex = getNextInstanceIndex(g, type);
        updatedGarments[garmentIdx] = {
          ...g,
          secondaryFabrics: [
            ...(g.secondaryFabrics || []),
            {
              type,
              instanceIndex,
              content: [createNewFiber()],
              weight: undefined,
            },
          ],
        };
        setValue('garments', updatedGarments);
      }
    } else {
      const currentSecondaryFabrics = singleGarmentSecondaryFabrics;
      const existingOfType = currentSecondaryFabrics.filter(
        (sf) => sf.type === type,
      );

      const legacyFields: Record<SecondaryFabricType, unknown> = {
        lining,
        contrast,
        filling,
        backing,
        coating,
      };
      const hasLegacyOfType = !!legacyFields[type];

      let instanceIndex: number;
      if (existingOfType.length > 0) {
        instanceIndex =
          Math.max(...existingOfType.map((sf) => sf.instanceIndex)) + 1;
      } else if (hasLegacyOfType) {
        instanceIndex = 2;
      } else {
        instanceIndex = 1;
      }

      setValue('secondaryFabrics', [
        ...currentSecondaryFabrics,
        {
          type,
          instanceIndex,
          content: [createNewFiber()],
          weight: undefined,
        },
      ]);
    }
  };

  const removeSecondaryFabric = (
    type: SecondaryFabricType,
    instanceIndex: number,
    isLegacy: boolean,
  ) => {
    if (isMultiGarment && garments && garment) {
      const updatedGarments = [...garments];
      const garmentIdx = updatedGarments.findIndex(
        (g) => g.garmentIndex === garment.garmentIndex,
      );

      if (garmentIdx !== -1) {
        const g = updatedGarments[garmentIdx];
        updatedGarments[garmentIdx] = {
          ...g,
          secondaryFabrics: (g.secondaryFabrics || []).filter(
            (sf) => !(sf.type === type && sf.instanceIndex === instanceIndex),
          ),
        };
        setValue('garments', updatedGarments);
        clearErrors(`garments.${garmentIdx}.secondaryFabrics`);
      }
    } else if (isLegacy) {
      setValue(type, undefined);
      clearErrors(type);
    } else {
      const updatedSecondaryFabrics = singleGarmentSecondaryFabrics.filter(
        (sf) => !(sf.type === type && sf.instanceIndex === instanceIndex),
      );
      setValue('secondaryFabrics', updatedSecondaryFabrics);
      clearErrors('secondaryFabrics');
    }
  };

  const availableFabricTypes = FABRIC_TYPE_OPTIONS;

  const getSecondaryFabricsToRender = () => {
    if (isMultiGarment && garment) {
      return (garment.secondaryFabrics || []).map((sf, sfIndex) => ({
        fabric: sf,
        sfIndex,
        isLegacy: false,
        legacyKey: undefined as SecondaryFabricType | undefined,
      }));
    }

    const allFabrics: Array<{
      fabric: {
        type: SecondaryFabricType;
        instanceIndex: number;
        content: CounterFiberContent[];
        weight?: number | string;
      };
      sfIndex: number;
      isLegacy: boolean;
      legacyKey: SecondaryFabricType | undefined;
    }> = [];

    singleGarmentSecondaryFabrics.forEach((sf, sfIndex) => {
      allFabrics.push({
        fabric: sf,
        sfIndex,
        isLegacy: false,
        legacyKey: undefined,
      });
    });

    if (lining) {
      allFabrics.push({
        fabric: {
          type: 'lining',
          instanceIndex: 1,
          content: lining.liningContent,
          weight: lining.liningWeight,
        },
        sfIndex: -1,
        isLegacy: true,
        legacyKey: 'lining',
      });
    }
    if (contrast) {
      allFabrics.push({
        fabric: {
          type: 'contrast',
          instanceIndex: 1,
          content: contrast.contrastContent,
          weight: contrast.contrastWeight,
        },
        sfIndex: -1,
        isLegacy: true,
        legacyKey: 'contrast',
      });
    }
    if (filling) {
      allFabrics.push({
        fabric: {
          type: 'filling',
          instanceIndex: 1,
          content: filling.fillingContent,
          weight: filling.fillingWeight,
        },
        sfIndex: -1,
        isLegacy: true,
        legacyKey: 'filling',
      });
    }
    // Legacy-only types (can view/edit/delete existing, but can't add new)
    if (backing) {
      allFabrics.push({
        fabric: {
          type: 'backing',
          instanceIndex: 1,
          content: backing.backingContent,
          weight: backing.backingWeight,
        },
        sfIndex: -1,
        isLegacy: true,
        legacyKey: 'backing',
      });
    }
    if (coating) {
      allFabrics.push({
        fabric: {
          type: 'coating',
          instanceIndex: 1,
          content: coating.coatingContent,
          weight: coating.coatingWeight,
        },
        sfIndex: -1,
        isLegacy: true,
        legacyKey: 'coating',
      });
    }

    return allFabrics;
  };

  const secondaryFabrics = getSecondaryFabricsToRender();

  const garmentIdx = useMemo(() => {
    if (!isMultiGarment || !garments || !garment) return -1;
    return garments.findIndex((g) => g.garmentIndex === garment.garmentIndex);
  }, [isMultiGarment, garments, garment]);

  const getFieldPath = (field: string) => {
    if (isMultiGarment && garmentIdx >= 0) {
      return `garments.${garmentIdx}.${field}`;
    }
    return field;
  };

  // Don't render if garment index is invalid in multi-garment mode
  if (isMultiGarment && garmentIdx < 0) {
    return null;
  }

  const title = isMultiGarment
    ? `#${(index ?? 0) + 1} Garment Fabric Details`
    : 'Garment Fabric Details';

  return (
    <div className="flex flex-col gap-5 p-5 rounded-xl border border-neutral-200 bg-neutral-100">
      <div className="flex justify-between items-center">
        <P weight="semibold" size="lg">
          {title}
        </P>
        {isMultiGarment && canRemove && formEnabled && (
          <Clickable color="error" onClick={removeGarment}>
            <P color="error" weight="medium">
              Remove Garment
            </P>
          </Clickable>
        )}
      </div>

      <div className="rounded-xl border border-neutral-200 p-5 bg-white flex flex-col gap-5">
        {isMultiGarment && (
          <div className="w-full">
            <FormSelect
              label="What part of the garment is this?"
              inputId={`garment-part-${garment?.garmentIndex}`}
              name="garmentPart"
              value={garment?.garmentPart || 'other'}
              options={GARMENT_PART_OPTIONS}
              onChange={(e) => updateGarmentPart(e.target.value)}
              placeholder="Select garment part"
              required
              disabled={!formEnabled}
              className="[&_select]:nu-text-base-secondary"
            />
          </div>
        )}

        {/* fabric_type is only rendered in single-garment mode; in multi-garment mode it's rendered once in QuoteTab */}
        {!isMultiGarment && (
          <ControlledFormInput
            control={control}
            name="fabric_type"
            label="Fabric Type"
            inputType="text"
            required
            disabled={!formEnabled}
          />
        )}
      </div>

      <div className="flex flex-col rounded-xl border border-neutral-200 p-5 bg-white">
        <div className="flex justify-between items-center w-full">
          <P weight="medium">Self (Shell)</P>
        </div>
        <hr className="border-t border-gray-200 my-5" />
        <P weight="medium">
          Self (Shell) Fabric Content
          <span className="text-red-500 ml-1">*</span>
        </P>
        <P color="disabled" size="xs">
          Total of all contents must equal 100%
        </P>
        <div className="mt-3 flex flex-col gap-5">
          <FiberContentDataGrid
            fieldPath={
              isMultiGarment
                ? getFieldPath('fabricContentList')
                : 'fabric_content_list'
            }
            fieldNames={{ fiberField: 'name', percentageField: 'percentage' }}
          />

          <div className="relative flex items-center gap-2 cursor-pointer select-none">
            <Checkbox
              checked={hasPolyurethaneCoating}
              onChangeValue={updateHasPolyurethaneCoating}
              disabled={!formEnabled}
              tabIndex={-1}
            />
            <span className="text-sm text-base-secondary">
              This fabric includes Polyurethane Coating
            </span>
          </div>

          <div className={isFabricWeightNotApplicable ? 'hidden' : ''}>
            <ControlledFormInput
              control={control}
              name={isMultiGarment ? getFieldPath('fabricWeight') : 'weight'}
              label="Self (Shell) Fabric Weight"
              inputType="number"
              required={!isFabricWeightNotApplicable}
              rules={
                isFabricWeightNotApplicable ? {} : validationRules('weight')
              }
              disabled={!formEnabled || isFabricWeightNotApplicable}
              inputProps={{
                className: 'w-full',
                placeholder: '0',
                min: '0.01',
                step: '0.01',
                endAdornment: (
                  <span className="text-gray-500 text-sm">gsm</span>
                ),
              }}
            />
          </div>

          <div className="relative flex items-center gap-2 cursor-pointer select-none">
            <Checkbox
              checked={isFabricWeightNotApplicable}
              onChangeValue={updateFabricWeightNotApplicable}
              disabled={!formEnabled}
              tabIndex={-1}
            />
            <span className="text-sm text-base-secondary">
              Self (Shell) Fabric Weight not applicable to this product.
            </span>
          </div>

          <ControlledFormInput
            control={control}
            name={
              isMultiGarment
                ? getFieldPath('fabricCodeName')
                : 'fabric_code_name'
            }
            label="Self (Shell) Fabric Code & Name"
            inputType="text"
            disabled={!formEnabled}
            inputProps={{
              className: 'w-full',
              placeholder: 'Enter fabric code and name',
            }}
          />
        </div>
      </div>

      {secondaryFabrics.map(({ fabric, sfIndex, isLegacy, legacyKey }) => {
        let fieldPrefix: string;
        if (isLegacy) {
          fieldPrefix = `${legacyKey}.${legacyKey}`;
        } else if (isMultiGarment) {
          fieldPrefix = `garments.${garmentIdx}.secondaryFabrics.${sfIndex}`;
        } else {
          fieldPrefix = `secondaryFabrics.${sfIndex}`;
        }
        const label = getSecondaryFabricLabel(
          fabric.type,
          fabric.instanceIndex,
        );

        return (
          <div
            key={
              isLegacy
                ? `legacy-${legacyKey}`
                : `${fabric.type}-${fabric.instanceIndex}`
            }
            className="flex flex-col rounded-xl border border-neutral-200 p-5 bg-white"
          >
            <div className="flex justify-between items-center w-full">
              <P weight="medium">{label}</P>
              {formEnabled && (
                <Clickable
                  color="error"
                  onClick={() =>
                    removeSecondaryFabric(
                      fabric.type,
                      fabric.instanceIndex,
                      isLegacy,
                    )
                  }
                >
                  <P color="error" weight="medium">
                    Remove{' '}
                    {fabric.type.charAt(0).toUpperCase() + fabric.type.slice(1)}
                  </P>
                </Clickable>
              )}
            </div>
            <hr className="border-t border-gray-200 my-5" />
            <P weight="medium">
              {label} Content
              <span className="text-red-500 ml-1">*</span>
            </P>
            <P color="disabled" size="xs">
              Total of all contents must equal 100%
            </P>
            <div className="mt-3">
              <FiberContentDataGrid
                fieldPath={
                  isLegacy
                    ? `${legacyKey}.${legacyKey}Content`
                    : `${fieldPrefix}.content`
                }
                fieldNames={{
                  fiberField: 'name',
                  percentageField: 'percentage',
                }}
              />

              <ControlledFormInput
                name={
                  isLegacy
                    ? `${legacyKey}.${legacyKey}Weight`
                    : `${fieldPrefix}.weight`
                }
                label={`${label} Weight`}
                control={control}
                inputType="number"
                disabled={!formEnabled}
                rules={{
                  valueAsNumber: true,
                  min: {
                    value: 0.01,
                    message: 'Weight must be greater than 0',
                  },
                }}
                inputProps={{
                  className: 'w-full mt-5',
                  placeholder: '0',
                  min: '0.01',
                  step: '0.01',
                  endAdornment: (
                    <span className="text-gray-500 text-sm">gsm</span>
                  ),
                }}
              />
            </div>
          </div>
        );
      })}

      {availableFabricTypes.length > 0 && formEnabled && (
        <Popover position="top-left" offset={8}>
          {({ close }) => (
            <>
              <Popover.Button size="md" color="secondary" className="w-fit">
                Add Fabrics
              </Popover.Button>
              <Popover.Content className="bg-white border border-neutral-200 rounded-xl py-2 min-w-[160px] shadow-lg">
                {availableFabricTypes.map((opt) => (
                  <button
                    key={opt.type}
                    type="button"
                    onClick={() => {
                      addSecondaryFabric(opt.type);
                      close();
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-neutral-100 text-left"
                  >
                    <Icon name="plus/plus-circle" size="16px" />
                    <P size="sm">{opt.label}</P>
                  </button>
                ))}
              </Popover.Content>
            </>
          )}
        </Popover>
      )}
    </div>
  );
}

export default GarmentSection;
